import { Controller } from "react-hook-form";
import { AnimatePresence, motion } from "motion/react";
import { Combobox } from "@/modern/components/ui/combobox";
import { cn } from "@/modern/utils";
import { FILE_DETAIL_FIELD_LABEL_CLS, FILE_DETAIL_FIELD_LABEL_INLINE_CLS, FILE_DETAIL_SELECT_CONTENT_CLS } from "../../fileDetailFormClasses";
import { usePromotePanel, PROMOTE_STEPS } from "../context/PromotePanelContext";
import { PromoteStepHelpTrigger } from "./PromoteStepHelpTrigger";

const VISIBILITY_OPTIONS = [
  { value: "public" as const, label: "Public" },
  { value: "restricted" as const, label: "Restricted" },
];

/** Public / Restricted segmented control + reason — bottom of Classify. */
export const PromoteVisibilityFields = () => {
  const { form, restrictionReasonOptions } = usePromotePanel();
  const { setValue } = form;
  const watchIsRestricted = form.watch("isRestricted");
  const { errors } = form.formState;

  return (
    <div className="space-y-1.5">
      <div className="flex min-h-7 items-center justify-between gap-2">
        <span className={FILE_DETAIL_FIELD_LABEL_INLINE_CLS}>Visibility</span>
        <PromoteStepHelpTrigger
          stepLabel="Visibility"
          helpText={PROMOTE_STEPS[0].visibilityHelpText}
        />
      </div>

      <Controller
        name="isRestricted"
        control={form.control}
        render={({ field }) => (
          <div
            className="flex w-full gap-0.5 rounded-full border border-input bg-muted p-0.5"
            role="radiogroup"
            aria-label="Visibility"
          >
            {VISIBILITY_OPTIONS.map((option) => {
              const isSelected =
                (option.value === "public" && !field.value) ||
                (option.value === "restricted" && field.value);

              return (
                <button
                  key={option.value}
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  onClick={() => {
                    const restricted = option.value === "restricted";
                    field.onChange(restricted);
                    if (!restricted) {
                      setValue("restrictionReason", null);
                    }
                  }}
                  className={cn(
                    "h-8 min-w-0 flex-1 rounded-full px-2 text-sm font-normal outline-none transition-colors",
                    "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background",
                    isSelected
                      ? "bg-primary text-primary-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        )}
      />

      <AnimatePresence initial={false}>
        {watchIsRestricted ? (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.15, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="pt-1.5">
              <label className={FILE_DETAIL_FIELD_LABEL_CLS}>Reason</label>
              <Controller
                name="restrictionReason"
                control={form.control}
                render={({ field }) => (
                  <Combobox
                    options={restrictionReasonOptions}
                    value={field.value ?? undefined}
                    onValueChange={(val) => field.onChange(val ?? null)}
                    placeholder="Select reason..."
                    searchPlaceholder="Search reasons..."
                    emptyMessage="No reasons found."
                    size="sm"
                    popoverContentClassName={FILE_DETAIL_SELECT_CONTENT_CLS}
                  />
                )}
              />
              {errors.restrictionReason ? (
                <p className="mt-1 text-sm text-destructive">{errors.restrictionReason.message}</p>
              ) : null}
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
};
